// World view helpers: buildings, roads, boulders, territory overlay, fog,
// deposit icons, ware-flow overlay. Render-side only. Client-side map caches
// are fed by worker events.

import * as THREE from 'three';
import buildingsJson from '../../data/buildings.json';
import { BuildingState, HEX_COL_SPACING, HEX_ROW_SPACING } from '../shared/constants';
import { BUILDING_STRIDE, UNIT_STRIDE } from '../shared/protocol';
import { ELEV_Y_SCALE } from './terrain';

const BUILDING_COLORS: Record<string, string> = {
  hq: '#d4a017',
  storehouse: '#8a6642',
  residence: '#c9a86a',
  woodcutter: '#3e7a33',
  forester: '#2e6e4e',
  sawmill: '#a86f32',
  quarry: '#7f7f7f',
  well: '#6fa8dc',
  farm: '#d9b656',
  windmill: '#c9c19a',
  bakery: '#b5651d',
  fishery: '#4d9fb8',
  hunter: '#7a5c3e',
  ranch: '#a3785a',
  coal_mine: '#3f3f3f',
  iron_mine: '#8a5a44',
  gold_mine: '#d4af37',
  smelter: '#b04a2a',
  toolmaker: '#6a7ab8',
  weaponsmith: '#8a3a3a',
  bowyer: '#5a7a3a',
  gold_smelter: '#caa72e',
  mint: '#e0c040',
  training_ground: '#b0603a',
  watchtower: '#aab2c0',
  athenaeum: '#7a6ab8',
  monument: '#e8e0c8',
  bandit_camp: '#5c3030',
};

/** extra mesh height per building id (default 1.4) */
const BUILDING_HEIGHTS: Record<string, number> = {
  hq: 2.4,
  watchtower: 2.8,
  training_ground: 1.2,
  bandit_camp: 1.1,
};

export const BUILDING_IDS: string[] = buildingsJson.buildings.map((b) => b.id);
export const BUILDING_NAMES: string[] = buildingsJson.buildings.map((b) => b.name);
export const BUILDING_SIZES: number[] = buildingsJson.buildings.map(
  (b) => ((b as Record<string, unknown>).size as number) ?? 2,
);
const DEPOSIT_KIND: Record<string, number> = { coal: 1, iron: 2, gold: 3 };
export const BUILDING_DEPOSIT: number[] = buildingsJson.buildings.map(
  (b) => DEPOSIT_KIND[((b as Record<string, unknown>).deposit as string) ?? ''] ?? 0,
);
export const BUILDING_NEEDS_GROUNDWATER: boolean[] = buildingsJson.buildings.map(
  (b) => (b as Record<string, unknown>).needsGroundwater === true,
);
export const BUILDING_RECIPE_LABELS: string[][] = buildingsJson.buildings.map((b) => {
  const raw = b as Record<string, unknown>;
  const recipes = raw.recipes as Array<{ outputs: Record<string, number> }> | undefined;
  if (recipes) return recipes.map((r) => Object.keys(r.outputs).join('+'));
  const labels: string[] = [];
  if (raw.outputs) labels.push(Object.keys(raw.outputs as object).join('+'));
  if (raw.altOutputs) labels.push(Object.keys(raw.altOutputs as object).join('+'));
  return labels;
});

interface BuildingView {
  mesh: THREE.Mesh;
  mat: THREE.MeshLambertMaterial;
  state: number;
  progress: number;
  owner: number;
}

/** faction/owner accent colors: player blue, enemy red, bandits dark red */
export const OWNER_COLORS = ['#4d8fd1', '#d14d4d', '#7a3535'];

export class BuildingsView {
  readonly group = new THREE.Group();
  private views = new Map<number, BuildingView>();
  private selectionRing: THREE.Mesh;
  private selectedId = -1;

  constructor(private heightAt: (x: number, y: number) => number) {
    this.group.name = 'buildings';
    const ringGeo = new THREE.RingGeometry(0.8, 1.0, 24);
    ringGeo.rotateX(-Math.PI / 2);
    this.selectionRing = new THREE.Mesh(
      ringGeo,
      new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.8 }),
    );
    this.selectionRing.visible = false;
    this.group.add(this.selectionRing);
  }

  setSelected(id: number): void {
    this.selectedId = id;
    this.selectionRing.visible = id !== -1;
  }

  update(buf: Float32Array, count: number): void {
    const seen = new Set<number>();
    for (let i = 0; i < count; i++) {
      const o = i * BUILDING_STRIDE;
      const id = buf[o];
      const defIdx = buf[o + 1];
      const x = buf[o + 2];
      const y = buf[o + 3];
      const size = buf[o + 4];
      const state = buf[o + 5];
      const progress = buf[o + 6];
      const owner = buf[o + 10];
      seen.add(id);

      let v = this.views.get(id);
      if (!v) {
        const defId = BUILDING_IDS[defIdx] ?? 'storehouse';
        const height = BUILDING_HEIGHTS[defId] ?? 1.4;
        const geo = new THREE.BoxGeometry(size * 0.9, height, size * 0.9);
        geo.translate(0, height / 2, 0);
        const mat = new THREE.MeshLambertMaterial({
          color: BUILDING_COLORS[defId] ?? '#9a8f7f',
          transparent: true,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.userData.buildingId = id;
        const cx = x + size / 2;
        const cz = y + size / 2;
        mesh.position.set(cx, this.heightAt(Math.round(cx), Math.round(cz)), cz);
        this.group.add(mesh);
        v = { mesh, mat, state: -1, progress: -1, owner: -1 };
        this.views.set(id, v);
      }
      if (v.state !== state || Math.abs(v.progress - progress) > 0.02 || v.owner !== owner) {
        v.state = state;
        v.progress = progress;
        if (v.owner !== owner) {
          v.owner = owner;
          // non-player buildings pick up their owner's accent tint
          const defId = BUILDING_IDS[defIdx] ?? 'storehouse';
          const base = new THREE.Color(BUILDING_COLORS[defId] ?? '#9a8f7f');
          if (owner > 0) base.lerp(new THREE.Color(OWNER_COLORS[owner] ?? '#d14d4d'), 0.45);
          v.mat.color.copy(base);
        }
        if (state === BuildingState.Blueprint) {
          v.mat.opacity = 0.3;
          v.mesh.scale.y = 0.25;
        } else if (state === BuildingState.Constructing) {
          v.mat.opacity = 0.75;
          v.mesh.scale.y = 0.25 + 0.75 * progress;
        } else if (state === BuildingState.Disabled) {
          v.mat.opacity = 0.45; // frozen ruins awaiting a claim (§7.6)
          v.mesh.scale.y = 0.8;
        } else {
          v.mat.opacity = 1;
          v.mesh.scale.y = 1;
        }
      }
      if (id === this.selectedId) {
        this.selectionRing.position.copy(v.mesh.position);
        this.selectionRing.position.y += 0.05;
        this.selectionRing.scale.setScalar(size);
      }
    }
    for (const [id, v] of this.views) {
      if (!seen.has(id)) {
        this.group.remove(v.mesh);
        v.mesh.geometry.dispose();
        this.views.delete(id);
      }
    }
  }

  /** meshes for raycast building selection */
  get pickables(): THREE.Object3D[] {
    return [...this.views.values()].map((v) => v.mesh);
  }
}

export class RoadsView {
  readonly group = new THREE.Group();
  private mat = new THREE.MeshLambertMaterial({ color: '#b09a6a' });
  private geo = new THREE.BoxGeometry(1, 0.06, 1);

  constructor(
    private mapW: number,
    private heightAt: (x: number, y: number) => number,
  ) {
    this.group.name = 'roads';
  }

  addTiles(tiles: ArrayLike<number>): void {
    for (let k = 0; k < tiles.length; k++) {
      const i = tiles[k];
      const x = i % this.mapW;
      const y = Math.floor(i / this.mapW);
      const m = new THREE.Mesh(this.geo, this.mat);
      m.position.set(x + 0.5, this.heightAt(x, y) + 0.03, y + 0.5);
      this.group.add(m);
    }
  }
}

export class BouldersView {
  mesh: THREE.InstancedMesh;
  private tiles: number[] = [];

  constructor(
    private mapW: number,
    private heightAt: (x: number, y: number) => number,
    stoneAmount: Uint8Array,
  ) {
    const geo = new THREE.DodecahedronGeometry(0.3, 0);
    const mat = new THREE.MeshLambertMaterial({ color: '#9c968a' });
    for (let i = 0; i < stoneAmount.length; i++) if (stoneAmount[i] > 0) this.tiles.push(i);
    this.mesh = new THREE.InstancedMesh(geo, mat, Math.max(1, this.tiles.length));
    this.mesh.name = 'boulders';
    this.rebuild(stoneAmount);
  }

  rebuild(stoneAmount: Uint8Array): void {
    this.tiles = [];
    for (let i = 0; i < stoneAmount.length; i++) if (stoneAmount[i] > 0) this.tiles.push(i);
    const m = new THREE.Matrix4();
    let n = 0;
    for (const i of this.tiles) {
      if (n >= this.mesh.instanceMatrix.count) break;
      const x = i % this.mapW;
      const y = Math.floor(i / this.mapW);
      const scale = 0.6 + (stoneAmount[i] / 7) * 0.6;
      m.makeScale(scale, scale, scale);
      m.setPosition(x + 0.5, this.heightAt(x, y) + 0.12, y + 0.5);
      this.mesh.setMatrixAt(n++, m);
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

export class TerritoryView {
  mesh: THREE.Mesh | null = null;
  readonly group = new THREE.Group();

  constructor(
    private mapW: number,
    private mapH: number,
    private hexOfTile: Int32Array,
    private heightAt: (x: number, y: number) => number,
  ) {
    this.group.name = 'territory';
  }

  rebuild(hexOwner: Int8Array): void {
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
    }
    const positions: number[] = [];
    const colors: number[] = [];
    const ownerColors = OWNER_COLORS.map((c) => new THREE.Color(c));
    for (let y = 0; y < this.mapH; y++) {
      for (let x = 0; x < this.mapW; x++) {
        const owner = hexOwner[this.hexOfTile[y * this.mapW + x]];
        if (owner < 0) continue;
        const c = ownerColors[owner] ?? ownerColors[1];
        const h = this.heightAt(x, y) + 0.02;
        const h10 = this.heightAt(x + 1, y) + 0.02;
        const h01 = this.heightAt(x, y + 1) + 0.02;
        const h11 = this.heightAt(x + 1, y + 1) + 0.02;
        positions.push(x, h, y, x, h01, y + 1, x + 1, h10, y);
        positions.push(x + 1, h10, y, x, h01, y + 1, x + 1, h11, y + 1);
        for (let k = 0; k < 6; k++) colors.push(c.r, c.g, c.b);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.14,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.group.add(this.mesh);
  }
}

/** deposit icons on surveyed hexes: coal/iron/gold octahedra at hex centers */
export class DepositsView {
  mesh: THREE.InstancedMesh;

  constructor(
    private hexCols: number,
    private hexRows: number,
    private heightAt: (x: number, y: number) => number,
  ) {
    const geo = new THREE.OctahedronGeometry(0.5, 0);
    const mat = new THREE.MeshLambertMaterial({ color: '#ffffff' });
    this.mesh = new THREE.InstancedMesh(geo, mat, hexCols * hexRows);
    this.mesh.count = 0;
    this.mesh.name = 'deposits';
  }

  rebuild(deposits: Int32Array, surveyed: Uint8Array): void {
    const KIND_COLORS = [null, new THREE.Color('#2b2b2b'), new THREE.Color('#b46a4a'), new THREE.Color('#ffd23e')];
    const m = new THREE.Matrix4();
    let n = 0;
    for (let h = 0; h < deposits.length; h++) {
      if (!surveyed[h]) continue;
      const kind = deposits[h] >>> 24;
      const amount = deposits[h] & 0xffffff;
      if (kind === 0 || amount === 0) continue;
      const r = Math.floor(h / this.hexCols);
      const c = h - r * this.hexCols;
      const cx = c * HEX_COL_SPACING + 6 + (r & 1) * 6;
      const cy = r * HEX_ROW_SPACING + 5;
      const scale = 0.6 + Math.min(1, amount / 800) * 0.7;
      m.makeScale(scale, scale, scale);
      m.setPosition(cx, this.heightAt(cx, cy) + 1.6, cy);
      this.mesh.setMatrixAt(n, m);
      this.mesh.setColorAt(n, KIND_COLORS[kind]!);
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

/** ware-flow overlay: line from each delivering porter to its destination */
export class FlowView {
  line: THREE.LineSegments;
  enabled = false;

  constructor(private mapW: number) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    this.line = new THREE.LineSegments(
      geo,
      new THREE.LineBasicMaterial({ color: '#ffe28a', transparent: true, opacity: 0.65 }),
    );
    this.line.frustumCulled = false;
    this.line.visible = false;
  }

  update(units: Float32Array, count: number, heightAt: (x: number, y: number) => number): void {
    this.line.visible = this.enabled;
    if (!this.enabled) return;
    const positions: number[] = [];
    for (let i = 0; i < count; i++) {
      const o = i * UNIT_STRIDE;
      const target = units[o + 5];
      if (target < 0) continue;
      const x = units[o + 2];
      const y = units[o + 3];
      const tx = (target % this.mapW) + 0.5;
      const ty = Math.floor(target / this.mapW) + 0.5;
      positions.push(x, heightAt(Math.round(x), Math.round(y)) + 0.6, y);
      positions.push(tx, heightAt(Math.round(tx), Math.round(ty)) + 0.6, ty);
    }
    this.line.geometry.dispose();
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    this.line.geometry = geo;
  }
}

/** fog of war (sim-authoritative from M3): overhead plane with per-tile alpha */
export class FogView {
  readonly mesh: THREE.Mesh;
  private tex: THREE.DataTexture;
  private data: Uint8Array<ArrayBuffer>;

  constructor(mapW: number, mapH: number) {
    this.data = new Uint8Array(mapW * mapH * 4);
    for (let i = 0; i < mapW * mapH; i++) this.data[i * 4 + 3] = 235; // start unexplored
    this.tex = new THREE.DataTexture(this.data, mapW, mapH, THREE.RGBAFormat);
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.flipY = false;
    this.tex.needsUpdate = true;
    const geo = new THREE.PlaneGeometry(mapW, mapH);
    const mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.rotation.x = -Math.PI / 2;
    // plane UV -> tile mapping: rotated plane's V axis runs opposite to +z
    this.mesh.scale.y = -1;
    this.mesh.position.set(mapW / 2, 13, mapH / 2);
    this.mesh.renderOrder = 500;
    this.mesh.name = 'fog';
  }

  /** copy the sim's per-tile FogState array (0 unexplored / 1 explored / 2 visible) */
  setFog(fog: Uint8Array): void {
    const n = fog.length;
    for (let i = 0; i < n; i++) {
      this.data[i * 4 + 3] = fog[i] === 2 ? 0 : fog[i] === 1 ? 115 : 235;
    }
    this.tex.needsUpdate = true;
  }
}

/** health bars: flat instanced quads above damaged units/buildings (M3) */
export class BarsView {
  readonly back: THREE.InstancedMesh;
  readonly front: THREE.InstancedMesh;
  private nb = 0;
  private nf = 0;
  private m = new THREE.Matrix4();
  private color = new THREE.Color();

  constructor(capacity = 2048) {
    const geoB = new THREE.PlaneGeometry(0.9, 0.14);
    geoB.rotateX(-Math.PI / 2);
    this.back = new THREE.InstancedMesh(
      geoB,
      new THREE.MeshBasicMaterial({ color: '#101010', transparent: true, opacity: 0.7, depthWrite: false }),
      capacity,
    );
    const geoF = new THREE.PlaneGeometry(0.9, 0.1);
    geoF.rotateX(-Math.PI / 2);
    geoF.translate(0.45, 0, 0); // scale from the left edge
    this.front = new THREE.InstancedMesh(
      geoF,
      new THREE.MeshBasicMaterial({ depthWrite: false }),
      capacity,
    );
    for (const mesh of [this.back, this.front]) {
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.renderOrder = 400;
    }
  }

  begin(): void {
    this.nb = 0;
    this.nf = 0;
  }

  /** hpPct 0-100; skips full-health bars */
  push(x: number, y: number, z: number, hpPct: number, width = 1): void {
    if (hpPct >= 100 || this.nb >= this.back.instanceMatrix.count) return;
    this.m.makeScale(width, 1, 1);
    this.m.setPosition(x, y, z);
    this.back.setMatrixAt(this.nb++, this.m);
    this.m.makeScale((width * Math.max(0, hpPct)) / 100, 1, 1);
    this.m.setPosition(x - (0.9 * width) / 2, y + 0.01, z);
    this.front.setMatrixAt(this.nf, this.m);
    this.color.setHSL(0.33 * (hpPct / 100), 0.9, 0.45); // green -> red
    this.front.setColorAt(this.nf, this.color);
    this.nf++;
  }

  end(): void {
    this.back.count = this.nb;
    this.back.instanceMatrix.needsUpdate = true;
    this.front.count = this.nf;
    this.front.instanceMatrix.needsUpdate = true;
    if (this.front.instanceColor) this.front.instanceColor.needsUpdate = true;
  }
}

/** transient combat tracers (arrows/bolts/volleys) fed by shot events (M3) */
export class ShotsView {
  readonly line: THREE.LineSegments;
  private shots: Array<{ fx: number; fy: number; tx: number; ty: number; kind: number; until: number }> = [];

  constructor() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    this.line = new THREE.LineSegments(
      geo,
      new THREE.LineBasicMaterial({
        color: '#ffe9b0',
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
      }),
    );
    this.line.frustumCulled = false;
    this.line.renderOrder = 450;
  }

  /** packed [fromX_q8, fromY_q8, toX_q8, toY_q8, kind] * n */
  addShots(packed: Int32Array, now: number): void {
    for (let k = 0; k + 4 < packed.length; k += 5) {
      this.shots.push({
        fx: packed[k] / 256,
        fy: packed[k + 1] / 256,
        tx: packed[k + 2] / 256,
        ty: packed[k + 3] / 256,
        kind: packed[k + 4],
        until: now + 220,
      });
      if (this.shots.length > 256) this.shots.shift();
    }
  }

  update(now: number, heightAt: (x: number, y: number) => number): void {
    this.shots = this.shots.filter((s) => s.until > now);
    const positions: number[] = [];
    for (const s of this.shots) {
      positions.push(s.fx, heightAt(Math.round(s.fx), Math.round(s.fy)) + 0.8, s.fy);
      positions.push(s.tx, heightAt(Math.round(s.tx), Math.round(s.ty)) + 0.6, s.ty);
    }
    this.line.visible = positions.length > 0;
    this.line.geometry.dispose();
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    this.line.geometry = geo;
  }
}

export { ELEV_Y_SCALE };
